import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { createUserWithEmailAndPassword } from "firebase/auth";
import { auth, db } from "../FirebaseConfig";
import { toast } from "react-toastify";
import { doc, setDoc } from "firebase/firestore";
import { Eye, EyeOff } from "lucide-react";

const MentorRegister = () => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();

  const validateEmail = (email) => {
    const emailRegex =
      /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;
    return emailRegex.test(email.toLowerCase());
  };

  const validatePhoneNumber = (phone) => {
    const phoneRegex = /^\+?([0-9]{1,4})?[-.\s]?([0-9]{6,14})$/;
    return phoneRegex.test(phone);
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    if (!validateEmail(email)) {
      toast.error(
        "Please enter a valid email address (e.g., user@example.com)."
      );
      setIsSubmitting(false);
      return;
    }
    if (!validatePhoneNumber(phone)) {
      toast.error("Please enter a valid phone number (e.g., +1234567890).");
      setIsSubmitting(false);
      return;
    }
    if (password.length < 6) {
      toast.error("Password must be at least 6 characters long.");
      setIsSubmitting(false);
      return;
    }
    try {
      const userCredential = await createUserWithEmailAndPassword(
        auth,
        email,
        password
      );
      const user = userCredential.user;
      await setDoc(doc(db, "mentors", user.uid), {
        name,
        email: email.toLowerCase(),
        phone,
        uid: user.uid,
        isVerified: false,
        createdAt: new Date().toISOString(),
      });
      const userData = {
        uid: user.uid,
        name,
        email: email.toLowerCase(),
        phone,
        role: "mentor",
      };
      localStorage.setItem("user", JSON.stringify(userData));
      toast.success("Account created successfully!");
      navigate("/mentor-form");
    } catch (error) {
      if (error.code === "auth/email-already-in-use") {
        toast.error("Email is already registered. Please log in.");
        navigate("/mentor-login");
      } else {
        toast.error(`Registration failed: ${error.message}`);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  //   useEffect(() => {
  //     if (auth.currentUser) {
  //       navigate("/mentor-form");
  //     }
  //   }, [navigate]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-100">
      <div className="md:flex w-full max-w-4xl bg-white shadow-lg rounded-lg">
        <div className="md:w-1/2 bg-white p-8">
          <div className="flex items-center justify-center mb-6">
            <img
              src={require("../assets/images/logo.jpg")}
              alt="Project Portal Logo"
              className="h-20"
            />
          </div>
          <h2 className="text-2xl font-semibold text-gray-800 mb-6 text-center">
            Mentor Register
          </h2>
          <form onSubmit={handleRegister} className="space-y-4">
            <input
              type="text"
              placeholder="Name"
              className="w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
            />
            <input
              type="email"
              placeholder="Email"
              className="w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
            <input
              type="text"
              placeholder="Phone (e.g., +1234567890)"
              className="w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              required
            />
            <div className="relative mb-4">
              <input
                type={showPassword ? "text" : "password"}
                placeholder="Password (minimum 6 characters)"
                className="w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
              >
                {showPassword ? (
                  <EyeOff className="h-5 w-5" />
                ) : (
                  <Eye className="h-5 w-5" />
                )}
              </button>
            </div>
            <button
              type="submit"
              className={`w-full py-3 rounded-lg text-white font-semibold ${
                isSubmitting
                  ? "bg-gray-400 cursor-not-allowed"
                  : "bg-orange-600 hover:bg-orange-700"
              } transition-colors`}
              disabled={isSubmitting}
            >
              {isSubmitting ? "Submitting..." : "Register"}
            </button>
          </form>
          <div className="mt-4 text-center">
            <p className="text-sm">
              Already have an account?{" "}
              <Link
                to="/mentor-login"
                className="text-blue-600 hover:underline font-semibold"
              >
                Log In
              </Link>
            </p>
          </div>
        </div>
        <div className="md:w-1/2 p-8 bg-gray-50">
          <h3 className="text-xl font-semibold text-gray-800 mb-4">
            Enjoying productivity and insights with Project Portal?
          </h3>
          <p className="mb-4">
            <span className="text-green-600 font-bold">Yes!</span> Contact us
            about adding your other projects to Project Portal.
          </p>
          <p>
            I have feedback.{" "}
            <a href="#" className="text-blue-600 font-semibold hover:underline">
              Share
            </a>{" "}
            your ideas and feedback with us.
          </p>
        </div>
      </div>
    </div>
  );
};

export default MentorRegister;
